var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { isEqual } from 'lodash';
import isEmpty from 'lodash.isempty';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '../../../abstractions/hooks/useQuery';
import { PreloadsContext } from '../../../contexts/preloads';
import { FactoryComponent } from '../../../factory';
import useDesign from '../../../hooks/useDesign';
import { SelectButton } from './components/SelectButton';
import { SelectOptions } from './components/SelectOptions';
import { BackgroundOverlay, OptionsWrapper } from './styled';
function findSelectedOption(options, selected, multiple) {
    var _a, _b;
    if (!options || typeof selected === 'undefined' || selected === null)
        return undefined;
    if (multiple) {
        // We only show text for when there's only one selection, otherwise we show the multiple config
        if (selected.length === 1) {
            return options[selected[0]] || ((_a = options.find) === null || _a === void 0 ? void 0 : _a.call(options, function (_a) {
                var value = _a.value;
                return selected[0] === value;
            }));
        }
        return undefined;
    }
    return options[selected] || ((_b = options.find) === null || _b === void 0 ? void 0 : _b.call(options, function (_a) {
        var value = _a.value;
        return selected === value;
    }));
}
function fetchOptionsIfNeeded(_a) {
    var options = _a.options, _b = _a.optionsFetchConfig, _c = _b === void 0 ? {} : _b, url = _c.url, opts = _c.opts, optionsFetchHandler = _a.optionsFetchHandler;
    if (!isEmpty(options))
        return options;
    if (optionsFetchHandler)
        return optionsFetchHandler({ url: url, opts: opts });
    if (!isEmpty(url)) {
        return fetch(url, opts).then(function (res) {
            if (!res.ok) {
                console.error('Something went wrong fetching options on Select component.');
                return Promise.resolve({});
            }
            return res.json();
        }, function (e) {
            console.error('Something went wrong fetching options on Select component.', e);
            return Promise.resolve({});
        });
    }
    return Promise.resolve({});
}
export var SelectComponent = function (_a) {
    var design = _a.design, selected = _a.selected, placeholder = _a.placeholder, options = _a.options, optionsFetchConfig = _a.optionsFetchConfig, optionsFetchHandler = _a.optionsFetchHandler, onChange = _a.onChange, className = _a.className, isFetching = _a.isFetching, props = __rest(_a, ["design", "selected", "placeholder", "options", "optionsFetchConfig", "optionsFetchHandler", "onChange", "className", "isFetching"]);
    var _b = useState(false), isOpen = _b[0], setIsOpen = _b[1];
    var _c = useDesign(design), components = _c.components, config = _c.config, schema = _c.schema;
    var multiple = Boolean(config === null || config === void 0 ? void 0 : config.multiple) || false;
    var configBasedDefault = selected || (config === null || config === void 0 ? void 0 : config.defaultValue);
    var defaultSelected = multiple && !Array.isArray(configBasedDefault) ? [configBasedDefault] : configBasedDefault;
    var _d = useState(defaultSelected), selectedValue = _d[0], setSelectedValue = _d[1];
    var _e = useQuery(['selectOptions', options], function () {
        return fetchOptionsIfNeeded(__assign({ options: options, optionsFetchHandler: optionsFetchHandler, optionsFetchConfig: optionsFetchConfig }, config));
    }, {
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        refetchOnMount: false,
    }), isLoading = _e.isLoading, data = _e.data;
    var selection = useMemo(function () { return findSelectedOption(data, selectedValue, multiple); }, [selectedValue, data, multiple]);
    var selectionForDisplay = multiple && selectedValue.length > 1 ? config.multiple : selection || '';
    // Propagate "selectedValue" local state change to parent
    var handleSelect = useCallback(function (value) {
        if (multiple) {
            setSelectedValue(function (prev) {
                var newValue = prev && prev.includes(value) ? prev.filter(function (el) { return el !== value; }) : __spreadArray(__spreadArray([], prev, true), [value], false);
                onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
                return newValue;
            });
        }
        else {
            setSelectedValue(value);
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
            setIsOpen(false);
        }
    }, [selectedValue]);
    // Consume "selected" prop change from parent to set local "selectedValue"
    useEffect(function () {
        if (!selected)
            return;
        if (multiple) {
            var newSelection = selected;
            if (!Array.isArray(newSelection))
                newSelection = [newSelection];
            if (!isEqual(selectedValue, newSelection))
                setSelectedValue(newSelection);
            return;
        }
        if (selected !== selectedValue) {
            setSelectedValue(selected);
        }
    }, [selected]);
    return (_jsxs(FactoryComponent, __assign({ className: className, schema: schema }, { children: [isOpen && _jsx(BackgroundOverlay, { role: "button", onClick: function () { return setIsOpen(false); } }), _jsx(SelectButton, { isLoading: isLoading || isFetching, active: isOpen, design: components.selectButton, selectedOption: selectionForDisplay, placeholder: placeholder || config.placeholder, onClick: function () { return setIsOpen(!isOpen); }, type: "button", disabled: props.disabled || config.disabled || false }), isOpen && (_jsx(OptionsWrapper, { children: _jsx(SelectOptions, { options: Object.values(data), design: components.options, onSelectOption: handleSelect, selectedOption: selectedValue, multiple: multiple }) }))] })));
};
export var PreloadedSelect = function (_a) {
    var design = _a.design, props = __rest(_a, ["design"]);
    var _b = useDesign(design).config, config = _b === void 0 ? {} : _b;
    var preloads = useContext(PreloadsContext).preloads;
    var preload = config.preload;
    var options = preloads[preload];
    return (_jsx(SelectComponent, __assign({ design: design, isFetching: isEmpty(options), options: options }, props)));
};
export var Select = function (_a) {
    var design = _a.design, value = _a.value, props = __rest(_a, ["design", "value"]);
    var _b = useDesign(design).config, config = _b === void 0 ? {} : _b;
    if (config.preload) {
        return _jsx(PreloadedSelect, __assign({ selected: value, design: design }, props));
    }
    return _jsx(SelectComponent, __assign({ selected: value, design: design }, props));
};
Select.displayName = 'Select';
